import { Button, mergeClasses, Spinner, Tooltip } from '@fluentui/react-components'
import { ArrowUpRegular, LibraryRegular, SaveRegular, StopFilled } from '@fluentui/react-icons'
import { useMutationState } from '@tanstack/react-query'
import type { FocusEvent as ReactFocusEvent } from 'react'
import { use, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import {
  CHAT_INPUT_CONTEXT_PANEL,
  CHAT_INPUT_EDITOR_MIN_HEIGHT_PX,
  measureChatInputMaxHeightPx,
  measureContextAutoCapPx,
} from '../../constants/layout'
import { ChatSessionContext } from '../../contexts/ChatSessionContext'
import { useChatContext, useContainerHeightReporting, useCreatePrompt } from '../../hooks'
import { useComposerBudget } from '../../hooks/useComposerBudget'
import { useComposerDrag } from '../../hooks/useComposerDrag'
import { useEditorContentHeight } from '../../hooks/useEditorContentHeight'
import { IMATCH_MUTATION_KEY } from '../../hooks/useImatch'
import { DEFAULT_MODEL, type ModelKey } from '../../types'
import { ContextBar } from '../ContextBar/ContextBar'
import { TrumbowygEditor } from '../TrumbowygEditor'
import { useStyles } from './ChatInput.styles'
import type { ComposerOverrides } from './composerDrag'
import {
  allocateComposerHeights,
  buildComposerDemand,
  fitContextOverrideToCards,
  persistContextOverrideAfterSend,
} from './composerDrag'
import { ModelSelectMenu } from './ModelSelectMenu'
import { PromptSlashMenu } from './PromptSlashMenu/PromptSlashMenu'
import { usePromptSlashMenu } from './PromptSlashMenu/usePromptSlashMenu'

interface ChatInputProps {
  onSend: (message: string, model: ModelKey) => void
  onSetContent?: (setContent: (content: string) => void, focus: () => void) => void
  isLoading?: boolean
  onUndoSend?: () => void
  onHeightChange?: (height: number) => void
}

const ChatInput = ({
  onSend,
  onSetContent,
  isLoading = false,
  onUndoSend,
  onHeightChange,
}: ChatInputProps) => {
  const chatSession = use(ChatSessionContext)
  const effectiveSelectedModel = chatSession?.selectedModel ?? DEFAULT_MODEL
  const systemPrompt = chatSession?.systemPrompt
  const reviewMode = chatSession?.reviewMode
  const effectiveOnSelectedModelChange = chatSession?.setSelectedModel ?? (() => undefined)

  const isImatchPending = useMutationState({
    filters: { mutationKey: IMATCH_MUTATION_KEY },
    select: (m) => m.state.status,
  }).some((s) => s === 'pending')

  const { t } = useTranslation()

  const { contextItems } = useChatContext()
  const hasContextItems = contextItems.length > 0

  const message = chatSession?.draftMessage ?? ''
  const setMessage = chatSession?.setDraftMessage
  const isContextBarExpanded = chatSession?.isContextBarExpanded ?? true
  const setIsContextBarExpanded = chatSession?.setIsContextBarExpanded ?? (() => undefined)
  // Context's full scrollable list content height, reported up by ContextBar. Feeds
  // contextDemand below so the list fits its content instead of always claiming its full
  // viewport-proportional cap.
  const [contextContentHeight, setContextContentHeight] = useState(0)
  const [maxComposerHeightPx, setMaxComposerHeightPx] = useState(measureChatInputMaxHeightPx)
  const [contextAutoCapPx, setContextAutoCapPx] = useState(measureContextAutoCapPx)

  // Re-measured after mount too: on the first render Home's footer isn't in the DOM yet.
  useEffect(() => {
    const syncToPageSize = () => {
      setMaxComposerHeightPx(measureChatInputMaxHeightPx())
      setContextAutoCapPx(measureContextAutoCapPx())
    }

    syncToPageSize()
    window.addEventListener('resize', syncToPageSize)
    return () => window.removeEventListener('resize', syncToPageSize)
  }, [])
  const classes = useStyles()
  const editorRef = useRef<{ focus: () => void } | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const contextSectionRef = useRef<HTMLDivElement>(null)
  const contextHeaderRef = useRef<HTMLDivElement>(null)
  const inputWrapperRef = useRef<HTMLDivElement>(null)
  const actionBarRef = useRef<HTMLDivElement>(null)
  const editorWrapperRef = useRef<HTMLDivElement>(null)
  const [isEditorFocused, setIsEditorFocused] = useState(false)
  const [inputCeiling, setInputCeiling] = useState<number | null>(null)
  const [contextHeaderReserveHeight, setContextHeaderReserveHeight] = useState(0)
  const [contextListGap, setContextListGap] = useState(0)
  // Resize affordance visibility is JS-driven (not CSS :hover/:focus-within): the handle
  // button lives outside the clipped chatBoxBody, so it shares no ancestor with contextSection
  // for a pure-CSS descendant selector to react to hovering/focusing context controls.
  const [isContextAreaActive, setIsContextAreaActive] = useState(false)
  const [isHandleActive, setIsHandleActive] = useState(false)
  const inputContentHeight = useEditorContentHeight(editorWrapperRef, message)
  const { mutate: savePrompt, isPending: isSavingPrompt } = useCreatePrompt()

  const focusEditor = useCallback(() => {
    setIsEditorFocused(true)
    editorRef.current?.focus()
  }, [])

  const {
    isOpen: isSlashMenuOpen,
    filtered,
    hasNoFavorites,
    highlightedIndex,
    handleSelect,
    onEditorKeyDown,
  } = usePromptSlashMenu({
    message,
    // Must be called here rather than in an onSelect wrapper in ChatInput — calling focus()
    // after applyPrompt() in the same event handler races with useEditorSync rewriting the DOM,
    // leaving the cursor at position 0. Firing it from inside the hook (after setDraftMessage
    // but before the next render) gives the rAF in focusEditor the correct DOM to work with.
    onAfterSelect: focusEditor,
  })

  // Office webview breaks CSS :focus-within — hide on wrapper mousedown; show on outside click or window blur.
  useEffect(() => {
    const handleDocumentMouseDown = (e: MouseEvent) => {
      const wrapper = editorWrapperRef.current
      if (!wrapper?.contains(e.target as Node)) {
        setIsEditorFocused(false)
      }
    }
    const handleWindowBlur = () => setIsEditorFocused(false)

    document.addEventListener('mousedown', handleDocumentMouseDown, true)
    window.addEventListener('blur', handleWindowBlur)
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown, true)
      window.removeEventListener('blur', handleWindowBlur)
    }
  }, [])

  const setEditorMessage = useCallback(
    (content: string) => {
      setMessage?.(content)
    },
    [setMessage]
  )

  /*
   * COMPOSER SIZING — the whole model, in one place.
   *
   * allocateComposerHeights splits `budget` between the context list and the editor.
   * contextHeight becomes the list's height; inputHeight becomes the editor's max-height.
   *
   * Each region has a natural demand (its content) and a floor. Dragging the handle adds
   * overrides on top: contextOverride for the list, inputCeiling for the editor.
   */
  const isContextListVisible = isContextBarExpanded && hasContextItems

  const composerBudget = useComposerBudget({
    // Only the header + the gap above the list are fixed overhead — the list's own height is
    // part of what gets split out of the budget below, not reserved ahead of it.
    contextFixedReserve: contextHeaderReserveHeight + (isContextListVisible ? contextListGap : 0),
    inputWrapperRef,
    actionBarRef,
    maxComposerHeight: maxComposerHeightPx,
    layoutRevision: `${isContextBarExpanded}:${hasContextItems}:${contextHeaderReserveHeight}:${contextListGap}`,
  })

  const composerDemand = buildComposerDemand({
    isContextListVisible,
    contextContentHeight,
    contextAutoCapPx,
    draftHtml: message,
    inputContentHeight,
    budget: composerBudget,
  })
  const { contextDemand, contextMax } = composerDemand
  const [contextOverride, setContextOverride] = useState<number | null>(null)
  const { contextHeight, inputHeight } = allocateComposerHeights(composerDemand, {
    contextOverride,
    inputCeiling,
  })

  // Not useCallback-wrapped: composerDemand/contextHeight/inputHeight are fresh every render
  // anyway (they're derived values, not state), so memoizing these getters would buy nothing.
  const getComposerDemand = () => composerDemand
  const getComposerStartHeights = () => ({ contextHeight, inputHeight })
  const handleComposerOverridesChange = useCallback((next: ComposerOverrides) => {
    setContextOverride(next.contextOverride)
    setInputCeiling(next.inputCeiling)
  }, [])

  const {
    isDragging: isComposerDragging,
    onMouseDown: handleComposerMouseDown,
    onKeyDown: handleComposerKeyDown,
  } = useComposerDrag({
    getStartHeights: getComposerStartHeights,
    getDemand: getComposerDemand,
    onOverridesChange: handleComposerOverridesChange,
    keyboardStep: CHAT_INPUT_CONTEXT_PANEL.keyboardStep,
  })

  // While the handle isn't held, the list is never taller than its cards. That springs a drag
  // back on release, and shrinks the list when cards are removed. Skipped while the list is
  // hidden, so a dragged height survives collapse → expand.
  useLayoutEffect(() => {
    if (isComposerDragging || !isContextListVisible) {
      return
    }
    setContextOverride((current) =>
      fitContextOverrideToCards(current, contextDemand, contextContentHeight, contextMax)
    )
  }, [isComposerDragging, isContextListVisible, contextDemand, contextContentHeight, contextMax])

  // Whether either region has anything left to give up or restore. Derived straight from this
  // render's allocated heights vs. their demands/floors — not from raw override state — so
  // there's nothing to keep in sync when content shrinks or a drag partially applies: the
  // moment nothing's compressed and nothing has room, the handle simply has nothing to do.
  const canShrink = contextHeight > contextDemand || inputHeight > CHAT_INPUT_EDITOR_MIN_HEIGHT_PX
  const canGrow =
    inputHeight < inputContentHeight ||
    contextHeight < Math.min(contextMax, composerBudget - inputHeight)
  const showResizeHandle = !isImatchPending && (canShrink || canGrow)

  const showResizeAffordance = isContextAreaActive || isHandleActive

  const handleContextAreaBlur = useCallback((event: ReactFocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as Node | null
    if (!next || !contextSectionRef.current?.contains(next)) {
      setIsContextAreaActive(false)
    }
  }, [])

  const handleSend = useCallback(() => {
    if (message) {
      onSend(message, effectiveSelectedModel)
      if (!chatSession?.keepMessageOnSend) {
        setMessage?.('')
        // Each new message starts fresh at content height; a drag-set ceiling from the previous
        // prompt shouldn't cap the next one.
        setInputCeiling(null)

        if (isContextListVisible) {
          setContextOverride(persistContextOverrideAfterSend(contextHeight, contextDemand))
        }
      }
    }
  }, [
    effectiveSelectedModel,
    message,
    onSend,
    setMessage,
    chatSession?.keepMessageOnSend,
    isContextListVisible,
    contextHeight,
    contextDemand,
  ])

  // Edit Message UX: expose setContent + focus to parent for click edit → load → focus flow
  useEffect(() => {
    if (onSetContent) {
      onSetContent((content: string) => setEditorMessage(content), focusEditor)
    }
  }, [onSetContent, setEditorMessage, focusEditor])

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target.classList.contains('trumbowyg-editor')) {
        setIsEditorFocused(true)
      }
      // Slash menu consumes Enter/Escape when open — don't let them fall through to send.
      if (onEditorKeyDown(e)) return
      if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        if (target.classList.contains('trumbowyg-editor')) {
          e.preventDefault()
          if (!isLoading) handleSend()
        }
      }
    },
    [onEditorKeyDown, handleSend, isLoading]
  )

  // don’t exceed viewport budget (maxReportedHeight)
  // once editor starts scrolling, don’t let outer container grow beyond previously reported size
  useContainerHeightReporting({
    rootRef,
    onHeightChange,
    // grow until editor becomes internally scrollable, then stabilize
    freezeGrowthWhenDescendantScrollableSelector: '.trumbowyg-editor',
    maxReportedHeight: maxComposerHeightPx,
  })

  useEffect(() => {
    const headerRow = contextHeaderRef.current
    if (!headerRow) {
      return
    }

    const measureContextLayout = () => {
      const contextBar = headerRow.parentElement
      if (!contextBar) {
        return
      }

      const barStyles = window.getComputedStyle(contextBar)
      const paddingTop = Number.parseFloat(barStyles.paddingTop || '0') || 0
      const paddingBottom = Number.parseFloat(barStyles.paddingBottom || '0') || 0
      const listGap = Number.parseFloat(barStyles.rowGap || barStyles.gap || '0') || 0

      setContextHeaderReserveHeight(headerRow.offsetHeight + paddingTop + paddingBottom)
      setContextListGap(listGap)
    }

    measureContextLayout()

    if (typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(measureContextLayout)
    observer.observe(headerRow)
    const contextBar = headerRow.parentElement
    if (contextBar) {
      observer.observe(contextBar)
    }

    return () => {
      observer.disconnect()
    }
  }, [hasContextItems, isImatchPending])

  // inputHeight already includes any drag-set inputCeiling.
  useLayoutEffect(() => {
    const editor = editorWrapperRef.current?.querySelector(
      '.trumbowyg-editor'
    ) as HTMLElement | null
    if (!editor) {
      return
    }

    editor.style.maxHeight = `${inputHeight}px`
  }, [inputHeight])

  return (
    <div
      ref={rootRef}
      className={mergeClasses(classes.chatBox, isImatchPending && classes.chatBoxImatchBorder)}
      style={
        {
          // Desired composer ceiling as a CSS var, consumed by the chatBox rule's maxHeight.
          '--chat-box-max-height': Number.isFinite(maxComposerHeightPx)
            ? `${maxComposerHeightPx}px`
            : 'none',
        } as React.CSSProperties
      }>
      {showResizeHandle ? (
        <button
          type="button"
          className={classes.resizeHandle}
          onMouseDown={handleComposerMouseDown}
          onKeyDown={handleComposerKeyDown}
          onMouseEnter={() => setIsHandleActive(true)}
          onMouseLeave={() => setIsHandleActive(false)}
          onFocus={() => setIsHandleActive(true)}
          onBlur={() => setIsHandleActive(false)}
          aria-label={t('chatInput.resizeContextHeight')}>
          <span
            className={mergeClasses(
              'resize-handle-grip',
              classes.resizeHandleGrip,
              showResizeAffordance && classes.resizeHandleGripVisible
            )}
          />
        </button>
      ) : null}
      <div className={classes.chatBoxBody}>
        <div
          ref={contextSectionRef}
          className={classes.contextSection}
          onMouseEnter={() => setIsContextAreaActive(true)}
          onMouseLeave={() => setIsContextAreaActive(false)}
          onFocusCapture={() => setIsContextAreaActive(true)}
          onBlurCapture={handleContextAreaBlur}>
          <ContextBar
            isExpanded={isContextBarExpanded}
            onExpandedChange={setIsContextBarExpanded}
            allocatedHeight={contextHeight}
            // Instant sizing belongs only to a drag tracking the pointer. Closing is a state
            // change, not tracking, so it glides shut on the collapse button's own transition.
            animateListResize={!isComposerDragging || !isContextListVisible}
            onListContentHeightChange={setContextContentHeight}
            headerRowRef={contextHeaderRef}
          />
        </div>
        <div ref={inputWrapperRef} className={classes.inputWrapper}>
          <span id="chat-input-description" className="sr-only">
            {t('chatInput.description')}
          </span>
          <div
            ref={editorWrapperRef}
            className={classes.editorWrapper}
            onMouseDownCapture={() => setIsEditorFocused(true)}
            onFocusCapture={() => setIsEditorFocused(true)}
            onBlurCapture={(e) => {
              const next = e.relatedTarget as Node | null
              if (!next || !editorWrapperRef.current?.contains(next)) {
                setIsEditorFocused(false)
              }
            }}>
            <PromptSlashMenu
              isOpen={isSlashMenuOpen}
              filtered={filtered}
              hasNoFavorites={hasNoFavorites}
              highlightedIndex={highlightedIndex}
              onSelect={handleSelect}
            />
            <TrumbowygEditor
              ref={editorRef}
              value={message}
              onChange={setEditorMessage}
              placeholder={t('chatInput.placeholder')}
              onKeyDown={handleKeyPress}
              ariaDescribedBy="chat-input-description"
            />
            {/* Rich placeholder with styled <kbd> — mounted when draft is empty and editor is not focused. Screen readers use the plain placeholder attribute on the editor. */}
            {!message && !isEditorFocused && (
              <span
                aria-hidden="true"
                className={`${classes.placeholderOverlay} chat-input-placeholder`}>
                <Trans i18nKey="chatInput.placeholderRich" components={{ kbd: <kbd /> }} />
              </span>
            )}
          </div>
        </div>
        <div ref={actionBarRef} className={classes.actionBar}>
          <div className={classes.leftActions}>
            <Tooltip content={t('chatInput.library')} relationship="label" appearance="inverted">
              <Link
                to="/prompts"
                className={classes.libraryLink}
                aria-label={t('chatInput.library')}>
                <LibraryRegular />
              </Link>
            </Tooltip>
            {message ? (
              isSavingPrompt ? (
                <Spinner size="tiny" aria-label={t('loading')} className={classes.saveSpinner} />
              ) : (
                <Tooltip
                  content={t('chatInput.savePrompt')}
                  relationship="label"
                  appearance="inverted">
                  <Button
                    appearance="subtle"
                    shape="circular"
                    icon={<SaveRegular />}
                    aria-label={t('chatInput.savePrompt')}
                    onClick={() =>
                      savePrompt({
                        userPrompt: message,
                        llmModel: effectiveSelectedModel,
                        systemPrompt,
                        reviewMode,
                      })
                    }
                  />
                </Tooltip>
              )
            ) : null}
          </div>
          <div className={classes.middleActions}>
            <ModelSelectMenu
              selectedModel={effectiveSelectedModel}
              onSelectedModelChange={effectiveOnSelectedModelChange}
              className={classes.modelMenuButton}
            />
          </div>
          {isLoading ? (
            <Tooltip content={t('chatInput.undoSend')} relationship="label" appearance="inverted">
              <Button
                className={classes.undoButton}
                appearance="subtle"
                icon={<StopFilled />}
                onClick={onUndoSend}
                shape="circular"
                aria-label={t('chatInput.undoSend')}
              />
            </Tooltip>
          ) : (
            <Tooltip content={t('chatInput.sendButton')} relationship="label" appearance="inverted">
              <Button
                className={classes.sendButton}
                appearance="primary"
                icon={<ArrowUpRegular />}
                onClick={handleSend}
                disabled={!message}
                shape="circular"
                aria-label={t('chatInput.sendButton')}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  )
}

export { ChatInput }
